import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, ShoppingBag, Loader2, Repeat } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { CATALOG_ITEMS, getUnitPrice, formatPrice } from "../data/catalog";
import { createCheckoutSession } from "../lib/api";

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { url } = await createCheckoutSession(items);
      window.location.href = url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
          <motion.aside
            className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[70] flex flex-col shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.4 }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="font-heading text-lg font-semibold text-navy-900">
                Your Cart {count > 0 && <span className="text-slate-400">({count})</span>}
              </h3>
              <button onClick={() => setIsOpen(false)} data-testid="cart-close" className="p-2 rounded-full hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-20">
                  <span className="w-16 h-16 rounded-full bg-sky-50 flex items-center justify-center">
                    <ShoppingBag className="w-7 h-7 text-royal" strokeWidth={1.5} />
                  </span>
                  <p className="text-slate-500 text-sm">Your cart is empty.</p>
                  <button onClick={() => { setIsOpen(false); navigate("/bundles"); }} className="text-royal font-semibold text-sm hover:underline">
                    Browse plans →
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((item) => {
                    const it = CATALOG_ITEMS[item.id];
                    const unit = getUnitPrice(item.id, item.mode);
                    const key = `${item.id}-${item.mode}`;
                    return (
                      <li key={key} className="flex gap-4 items-center" data-testid={`cart-item-${key}`}>
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-sky-50 to-white border border-slate-100 flex items-center justify-center shrink-0 p-2">
                          <img src="/images/brand/icon_b.png" alt="" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-navy-900 truncate">{it.name}</p>
                          <p className="text-xs text-slate-400">{it.supply}</p>
                          {item.mode === "subscribe" ? (
                            <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-royal bg-sky-50 px-2 py-0.5 rounded-full">
                              <Repeat className="w-3 h-3" /> Subscription · {it.frequency}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                              One-time
                            </span>
                          )}
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex items-center border border-slate-200 rounded-full">
                              <button onClick={() => updateQty(item.id, item.mode, item.quantity - 1)} className="p-1.5" data-testid={`cart-dec-${key}`}>
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="w-6 text-center text-sm" data-testid={`cart-qty-${key}`}>{item.quantity}</span>
                              <button onClick={() => updateQty(item.id, item.mode, item.quantity + 1)} className="p-1.5" data-testid={`cart-inc-${key}`}>
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <button onClick={() => removeItem(item.id, item.mode)} className="text-xs text-slate-400 hover:text-red-500" data-testid={`cart-remove-${key}`}>
                              Remove
                            </button>
                          </div>
                        </div>
                        <span className="font-semibold text-sm text-navy-900">{formatPrice(unit * item.quantity)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-slate-100 px-6 py-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Subtotal</span>
                  <span className="font-heading text-xl font-bold text-navy-900" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                </div>
                <p className="text-xs text-slate-400">Shipping & taxes calculated at checkout.</p>
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="cart-checkout-button"
                  className="w-full bg-royal text-white rounded-full px-8 py-4 text-base font-medium hover:bg-royal-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</> : "Checkout"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
